import clsx from 'clsx'
import { AnimatePresence, motion } from 'framer-motion'
import { useLocale } from 'next-intl'
import { usePathname } from 'next/navigation'
import React, { useEffect, useRef, useState } from 'react'

import Button from '@/components/atoms/Button'
import Icon from '@/components/atoms/Icon'
import { ButtonLabel } from '@/components/atoms/Typography'

import DropDownProvider from './context'
import styles from './Dropdown.module.scss'

const DropDown = ({
  children,
  trigger,
  triggerAdaptWidth = false,
  triggerProps = {},
  onClick,
  initialState = false,
  className,
}: {
  children: React.ReactNode
  trigger: React.ReactNode
  triggerAdaptWidth?: boolean
  triggerProps?: React.HTMLAttributes<HTMLButtonElement>
  onClick?: () => void
  initialState?: boolean
  className?: string
}) => {
  if (!trigger) throw new Error('Trigger element is required')
  const [isOpen, setIsOpen] = useState(initialState)

  const containerRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const locale = useLocale()
  const pathname = usePathname()

  const onTriggerClick: React.MouseEventHandler<HTMLElement> = () => {
    setIsOpen(!isOpen)
    if (onClick) onClick()
  }

  useEffect(() => {
    let w = 'auto'
    if (
      !!isOpen &&
      containerRef.current &&
      triggerRef.current &&
      triggerAdaptWidth
    ) {
      const { width } = containerRef.current.getBoundingClientRect()
      w = `${width}px`
      triggerRef.current.style.width = w
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  useEffect(() => {
    if (isOpen) setIsOpen(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, locale])

  return (
    <DropDownProvider
      setOpen={setIsOpen}
      isOpen={isOpen}
    >
      <div
        className={clsx(styles.dropdown, className, !!isOpen && styles.open)}
      >
        <Button
          variant='secondary'
          theme='dark'
          ref={triggerRef}
          onClick={onTriggerClick}
          className={clsx(styles.dropdownTrigger)}
          Icon={() => <Icon iconName='ChevronDown' />}
          {...triggerProps}
        >
          <ButtonLabel>{trigger}</ButtonLabel>
        </Button>
        <div
          className={clsx(
            'dropdown-list-container',
            styles.dropdownListContainer
          )}
          ref={containerRef}
        >
          <AnimatePresence>
            {isOpen && (
              <motion.div
                style={{ width: '100%' }}
                layout
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <ul className={styles.dropdownList}>{children}</ul>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </DropDownProvider>
  )
}

export default DropDown
